import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import ValueProps from '../ValueProps';
import ExternalCalSelection from '../ExternalCalSelection';
import LearnMoreButton from '../LearnMoreButton';
import CalSelectDialogContinueButton from '../CalSelectDialogContinueButton';

import { useToast } from '@ps-design-system/components/Toast';
import { useMediaQuery } from '@ps-design-system/lib/hooks/use-media-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter
} from '@ps-design-system/components/Dialog';
import { Button } from '@ps-design-system/components/Button';
import ChevronLeft from '@ps-design-system/icons/ChevronLeft/ChevronLeft24';

import useSyncFlowNav, { NavSteps } from '../../hooks/useSyncFlowNav';
import CalendarSelection from '../CalendarSelection';
import { SyncStatus } from '../../../connect-calendar/util/syncStatus';

import { Subject } from 'rxjs';
import ICalendarSync from '../ICalendarSync';
import useCalendarStore from '../../../store/calendar/useCalendarStore';
import CalSyncDialogStrings, { TitleStepMap } from './strings';
import useSyncFlowAnalytics from '../../hooks/useSyncFlowAnalytics';

export const SyncFlowEventSubject$ = new Subject();

const CalSyncDialog = (props) => {
  const {
    stepOverride = null // This forces a specific step to show when the dialog trigger button is clicked (e.g., SELECT_CALENDAR).
  } = props;

  const { toast } = useToast();
  const isMobile = useMediaQuery('(max-width: 576px)');

  const openedFromButtonRef = useRef(false);

  const syncStatus = useCalendarStore((state) => state.syncStatus);
  const { setConnectSpace } = useCalendarStore();

  const {
    TrackGetStartedClick,
    TrackSyncFailed,
    TrackCalendarSyncCompleted
  } = useSyncFlowAnalytics();

  const {
    navToStep,
    navToPreviousStep,
    clearNav,
    currentStep
  } = useSyncFlowNav();

  useEffect(() => {
    const subscription = SyncFlowEventSubject$.subscribe((event) => {
      if ('error' === event.type || 'no_cals_to_sync' === event.type) {
        toast({
          title: '',
          description: 'error' === event.type ? CalSyncDialogStrings.failureToast : CalSyncDialogStrings.noCalsToSyncToast,
          variant: 'destructive',
          duration: 5000
        });
        TrackSyncFailed(event.errorMessage ?? 'no cals to sync');
        clearNav();
      } else if ('success' === event.type) {
        toast({
          title: '',
          description: CalSyncDialogStrings.successToast,
          variant: 'success',
          duration: 5000
        });
        TrackCalendarSyncCompleted(event.calType);
        setConnectSpace(event.syncedSpaceId);
        clearNav();
      } else if ('redirect' === event.type) {
        window.location.href = event.link;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const renderStep = () => {
    switch (currentStep) {
    case NavSteps.VALUE_PROPS:
      return <ValueProps/>;
    case NavSteps.EXTERNAL_CAL_SELECTION:
      return <ExternalCalSelection/>;
    case NavSteps.SELECT_CALENDAR:
      return <CalendarSelection/>;
    case NavSteps.I_CALENDAR:
      return <ICalendarSync/>;
    default:
      return null;
    }
  };

  const openDialogFromButton = () => {
    openedFromButtonRef.current = true;
    TrackGetStartedClick();
    navToStep(stepOverride ?? NavSteps.VALUE_PROPS);
  };

  const openDialog = (openedFromButtonRef.current && null !== currentStep)
    || (currentStep === NavSteps.SELECT_CALENDAR && SyncStatus.AUTHORIZED === syncStatus);

  return (
    <Dialog open={openDialog} onOpenChange={(open) => {
      if (!open) {
        openedFromButtonRef.current = false;
        clearNav();
      }
    }}>
      <Button onClick={openDialogFromButton} className="cal-sync-get-started-btn">
        {syncStatus === SyncStatus.AUTHORIZED ? CalSyncDialogStrings.continueButton : CalSyncDialogStrings.getStartedButton}
      </Button>
      <DialogContent className="CalSyncDialogContent" size={isMobile ? 'full' : 'sm'}>
        <DialogHeader
          headerAlignment="left"
          showCloseButton
          className="CalSyncDialogContent_header-padding"
        >
          <div className="CalSyncDialogContent_heading-container">
            {currentStep === NavSteps.I_CALENDAR &&
            <Button
              onClick={navToPreviousStep}
              variant="text-only"
              className="CalSyncDialogContent_back-button"
            >
              <ChevronLeft/>
            </Button>
            }
            <DialogTitle className="CalSyncDialogContent_dialog_title">
              {TitleStepMap[currentStep]}
            </DialogTitle>
          </div>
        </DialogHeader>
        <DialogBody
          showScrollBorder={false}
        >
          {renderStep()}
        </DialogBody>
        <DialogFooter>
          <div className="CalSyncActionButtons">
            <LearnMoreButton/>
            <CalSelectDialogContinueButton/>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

CalSyncDialog.propTypes = {
  stepOverride: PropTypes.string
};

export default CalSyncDialog;
